// Splits a rendered billing document sheet into explicit pages.
//
// This file runs in two very different engines and must stay ES5. The live preview calls
// it from the parent window against the iframe's document (scripts are blocked inside the
// frame, so it cannot run there itself), and wkhtmltopdf 0.12.6 runs the very same source
// inline from `body onload` via Billing::PaginatorSource — QtWebKit parses no const/let,
// no arrow functions and no template literals, and has no dependable Intl. Nothing here
// may use them.
//
// Deciding the breaks here rather than leaving them to the engine is what makes three
// things possible that QtWebKit CSS cannot express, all measured and documented in
// _styles.html.erb: an Uebertrag carried across the break, a Titel row bound to its first
// position, and an item row bound to its Langtext.
//
// It fails open. Every bail-out below leaves the untouched flow in place, which renders
// exactly as it did before this file existed — that is the property that makes it safe to
// put JavaScript in the path that produces a legally binding document.
//
// The lint exception is not laziness. Probed against the 0.12.6 binary this project ships,
// QtWebKit rejects const, let, arrow functions, template literals, destructuring and
// spread, and has no Array.from. Everything the rest of the codebase's ESLint rules ask
// for is a syntax error here, and this source reaches the engine verbatim — esbuild never
// sees the copy the PDF runs.
/* eslint-disable no-var */

// Far above any real document (a DIN sheet holds ~40 rows); this only bounds the damage
// if a measurement returns nonsense and every unit starts landing on its own page.
var MAX_PAGES = 80

export function paginateBillingDocument(doc) {
  try {
    return run(doc)
  } catch (error) {
    warn(error)
    return 0
  }
}

function warn(detail) {
  if (window.console && window.console.warn) window.console.warn("billing paginator", detail)
}

function run(doc) {
  var root = doc.querySelector(".bd-pages")
  if (!root || root.getAttribute("data-paginated")) return 0

  var bd = root.querySelector(".bd")
  var body = bd && bd.querySelector(".bd-body")
  if (!body) return 0

  // The unpaginated stand-ins, dropped before anything is measured. They only exist so the
  // pane still shows its bands if this file never runs or bails out; once it does run, the
  // per-page bands in .bd-page-template replace them. Removing rather than hiding them
  // keeps the first .bd-page as :first-child, which is what the layout's "no running
  // header on page 1" rule matches on.
  removeAll(root.querySelectorAll(".bd-fallback"))

  // The page budget is authored as CSS in the layout, from Billing::Din5008 and this
  // document's own footer height. Reading it back off a rendered probe keeps both the
  // millimetres and the mm-to-px ratio out of this file.
  var probe = root.querySelector(".bd-probe")
  var contentPx = probe ? probe.getBoundingClientRect().height : 0
  removeAll([probe])
  if (!(contentPx > 100)) return 0

  var table = body.querySelector("table.bd-items")
  var units = collectUnits(body, table)
  if (!units.length) return 0

  var pages = assignPages(units, bd, table, contentPx)
  if (!pages) return 0
  // Far past any real document, so this is a measurement that went wrong rather than a
  // long Leistungsverzeichnis. Bailing out renders the whole sheet unpaginated and without
  // an Uebertrag, which is too quiet a failure to leave unsaid.
  if (pages.length > MAX_PAGES) {
    warn("gave up at " + pages.length + " pages (max " + MAX_PAGES + ")")
    return 0
  }

  build(doc, root, bd, body, table, pages)
  root.setAttribute("data-paginated", "1")
  return pages.length
}

// ---------------------------------------------------------------------------
// Break units: the smallest things the engine is allowed to break between.
// Anything not listed is atomic by omission, which is the safe direction — refusing a
// break the printer would take costs one unit of accuracy, inventing one the printer will
// not take is wrong for the whole rest of the document.

function collectUnits(body, table) {
  var units = []
  var blocks = body.children

  for (var i = 0; i < blocks.length; i++) {
    var block = blocks[i]
    if (block === table) {
      pushRowUnits(units, table)
    } else {
      units.push({ kind: "block", nodes: [block] })
    }
  }
  return units
}

function pushRowUnits(units, table) {
  var tbody = table.tBodies && table.tBodies[0]
  if (!tbody) return

  var rows = tbody.rows
  var i = 0
  while (i < rows.length) {
    var nodes = [rows[i]]
    // A Titel row carries no amount of its own; stranded at the foot of a page it reads
    // as a heading for nothing. Bind it to the position it introduces (but never to
    // another Titel — a sub-Titel has to be free to start a page).
    if (hasClass(rows[i], "grp") && rows[i + 1] && !hasClass(rows[i + 1], "grp")) {
      i += 1
      nodes.push(rows[i])
    }
    // tr.has-note / tr.has-img have border-bottom: 0 — split from its Langtext or its
    // Positionsbild, an item row leaves a borderless line hanging at a page foot and a
    // description orphaned at the next page's head. Both companions follow their item
    // row directly (note first, then img), so absorbing every trailing one binds the
    // three into one unit.
    while (rows[i + 1] && (hasClass(rows[i + 1], "note") || hasClass(rows[i + 1], "img"))) {
      i += 1
      nodes.push(rows[i])
    }
    units.push({ kind: "row", nodes: nodes })
    i += 1
  }
}

// ---------------------------------------------------------------------------
// Assignment. Every unit already has a position in the single continuous flow, so this
// walks that flow with a cursor rather than re-measuring after each move.
//
// All getBoundingClientRect() reads happen here, before build() writes anything —
// interleaving the two would turn this into one synchronous relayout per unit.

function assignPages(units, bd, table, contentPx) {
  var origin = bd.getBoundingClientRect().top
  var thead = table && table.tHead
  // A continuation table repeats its column headers, and that repetition costs height the
  // original flow never spent.
  var theadPx = thead ? thead.getBoundingClientRect().height : 0

  var measured = []
  for (var i = 0; i < units.length; i++) {
    measured.push({
      top: units[i].nodes[0].getBoundingClientRect().top - origin,
      bottom: units[i].nodes[units[i].nodes.length - 1].getBoundingClientRect().bottom - origin,
    })
  }

  // First pass with no reservation, only to find out whether the Positionen table spans
  // pages at all. If it does, the second pass has to leave room for the Uebertrag rows —
  // they are appended after assignment and would otherwise spend budget nobody reserved,
  // which is what makes wkhtmltopdf split the page again and strand a two-line tail.
  // A table without data-carry-label (a Blanko sheet) gets no Uebertrag rows, so it must
  // not pay for them either — addCarryForward keys off the same attribute.
  var pages = walk(units, measured, contentPx, theadPx, 0)
  var carries = !!(table && table.getAttribute("data-carry-label"))
  var reserve = carries && rowPages(pages) > 1 ? 2 * carryRowPx(table) : 0
  return reserve > 0 ? walk(units, measured, contentPx, theadPx, reserve) : pages
}

// `reserve` covers one Uebertrag at the page's head and one at its foot. A page uses at
// most both, so reserving both can never overshoot — at the price of one unused row on the
// first and last page of the table, which is the cheap side of the trade.
function walk(units, measured, contentPx, theadPx, reserve) {
  var pages = [[]]
  var cursor = 0
  var avail = contentPx
  var pageHasRows = false

  for (var j = 0; j < units.length; j++) {
    var current = pages[pages.length - 1]
    var row = units[j].kind === "row"
    // What an empty *continuation* page could offer this unit, and only ever used for the
    // `fits` test below: a break can move a unit onto a page after the first and nowhere
    // else, and such a page always repeats the thead and always pays for both Uebertrag
    // rows. Deliberately not the figure `avail` is reset to — that one is charged below.
    var fresh = contentPx - (row ? theadPx + reserve : 0)
    // A unit that would not fit on an empty page either must not trigger a break: moving
    // it empties the page it leaves and still overflows the one it lands on. Let it start
    // where it is and run over, which is what the engine did before this file existed.
    var fits = measured[j].bottom - measured[j].top <= fresh
    // `current.length > 0` is the other half of the progress guarantee.
    if (measured[j].bottom - cursor > avail && current.length > 0 && fits) {
      current = []
      pages.push(current)
      pageHasRows = false
      // The margin above a break is spent on the page being left, exactly as a real page
      // fragmentation discards it.
      cursor = measured[j].top
      // The whole page, before any row cost. The cost is charged exactly once, below,
      // whether this page opens with a row or only reaches the table after some prose.
      avail = contentPx
    }
    // The first row on a page is what makes it pay, so the charge belongs here rather than
    // on the break: a page that starts with prose and only then reaches the table gets a
    // re-opened table with a repeated thead just the same. Page 1 is exempt from theadPx
    // alone — its thead is the original one, already inside `measured`.
    if (row && !pageHasRows) {
      pageHasRows = true
      avail -= reserve + (pages.length > 1 ? theadPx : 0)
    }
    current.push(units[j])
  }
  return pages
}

function rowPages(pages) {
  var count = 0
  for (var p = 0; p < pages.length; p++) {
    for (var i = 0; i < pages[p].length; i++) {
      if (pages[p][i].kind === "row") {
        count += 1
        break
      }
    }
  }
  return count
}

// The Uebertrag row itself, measured once on a probe at the end of the original table — after
// every unit is measured, so appending it moves nothing already read. An item row used to be its
// proxy, but the Uebertrag carries a rule and more padding of its own (_styles: tr.carry), 2 px
// a row: enough for a page packed with a Textposition's short blocks to overflow by its foot
// Uebertrag and spill it onto a sheet of its own.
function carryRowPx(table) {
  var tbody = table.tBodies && table.tBodies[0]
  if (!tbody) return 0

  var label = table.getAttribute("data-carry-label")
  var probe = carryRow(table.ownerDocument, label, "0", countColumns(table))
  tbody.appendChild(probe)
  var height = probe.getBoundingClientRect().height
  tbody.removeChild(probe)
  return height
}

// ---------------------------------------------------------------------------
// Rebuild the flow as explicit .bd-page siblings.

function build(doc, root, bd, body, table, pages) {
  var template = doc.querySelector(".bd-page-template")
  var built = []

  for (var p = 0; p < pages.length; p++) {
    // Page 1 reuses the original .bd so the Falzmarken, the Briefkopf zone and the
    // bd--stationery modifier stay exactly where they were. Later pages get shallow
    // clones, so their classes come along without anything being hardcoded here.
    //
    // Its .bd-body is replaced rather than reused: filling a page appends units in order,
    // and anything bound for a later page that was left sitting in the original body would
    // end up in front of them — which put the Positionen thead above the Betreffzeile.
    var host = p === 0 ? bd : bd.cloneNode(false)
    var hostBody = body.cloneNode(false)
    if (p === 0 && body.parentNode) body.parentNode.removeChild(body)
    host.appendChild(hostBody)

    var page = makePage(doc, template)
    contentSlot(page).appendChild(host)
    // `open` is the tbody currently accepting rows and is cleared by any block that
    // follows them; `rows` remembers the last one that actually took rows, which is where
    // an Uebertrag has to be appended.
    built.push({ page: page, body: hostBody, open: null, rows: null })

    fillPage(doc, built[p], pages[p], table, p > 0)
    root.appendChild(page)
  }

  addCarryForward(doc, built, pages, table)
  markSummaryPageStarts(built, pages)
  numberPages(built)
}

function fillPage(doc, target, units, table, continuation) {
  for (var i = 0; i < units.length; i++) {
    var unit = units[i]
    if (unit.kind === "row") {
      if (!target.open) {
        target.open = openTable(doc, target.body, table, continuation)
        target.rows = target.open
      }
      appendAll(target.open, unit.nodes)
    } else {
      // A block after rows closes the table: anything following the Positionen (Summen,
      // Zahlung, Schlusstext) is a sibling of it, never a row inside it.
      target.open = null
      appendAll(target.body, unit.nodes)
    }
  }
}

// Re-open the Positionstabelle on a continuation page with the same fixed mm colgroup and
// the same column headers, so the columns line up across the break and the reader still
// knows which number is which.
function openTable(doc, host, table, continuation) {
  if (!continuation) {
    host.appendChild(table)
    return table.tBodies[0]
  }

  var clone = table.cloneNode(false)
  clone.className = clone.className + " bd-items--cont"

  var colgroup = table.querySelector("colgroup")
  if (colgroup) clone.appendChild(colgroup.cloneNode(true))
  if (table.tHead) clone.appendChild(table.tHead.cloneNode(true))

  var tbody = doc.createElement("tbody")
  clone.appendChild(tbody)
  host.appendChild(clone)
  return tbody
}

// ---------------------------------------------------------------------------
// Uebertrag. Each item row carries data-carry: the running net up to and including that
// row, already formatted by Ruby. Copying that string is the whole job — no arithmetic and
// no Intl, which is what makes this safe in QtWebKit.

function addCarryForward(doc, built, pages, table) {
  if (!table) return
  var label = table.getAttribute("data-carry-label")
  if (!label) return

  var columns = countColumns(table)
  // A page holding only a Textposition's blocks (`txt`) has no carry of its own; the running net
  // is still the one the last position before it left.
  var amount = null

  for (var p = 0; p < pages.length - 1; p++) {
    amount = lastCarry(pages[p]) || amount

    // Only when the table really spans the break. A page whose rows ended and was then
    // filled with the Summenblock needs no Uebertrag; the totals say it better.
    if (!built[p].rows || !built[p + 1].rows) continue
    if (!amount) continue

    built[p].rows.appendChild(carryRow(doc, label, amount, columns))
    var next = built[p + 1].rows
    next.insertBefore(carryRow(doc, label, amount, columns), next.firstChild)
  }
}

function carryRow(doc, label, amount, columns) {
  var row = doc.createElement("tr")
  row.className = "carry"

  var labelCell = doc.createElement("td")
  labelCell.className = "carry-label"
  labelCell.setAttribute("colspan", String(Math.max(columns - 1, 1)))
  labelCell.appendChild(doc.createTextNode(label))

  var valueCell = doc.createElement("td")
  valueCell.className = "num"
  valueCell.appendChild(doc.createTextNode(amount))

  row.appendChild(labelCell)
  row.appendChild(valueCell)
  return row
}

function lastCarry(units) {
  for (var i = units.length - 1; i >= 0; i--) {
    if (units[i].kind !== "row") continue
    for (var j = units[i].nodes.length - 1; j >= 0; j--) {
      var carry = units[i].nodes[j].getAttribute && units[i].nodes[j].getAttribute("data-carry")
      if (carry) return carry
    }
  }
  return null
}

function countColumns(table) {
  var header = table.tHead && table.tHead.rows[0]
  return header ? header.cells.length : 2
}

// ---------------------------------------------------------------------------

// The running header already draws a rule a few millimetres above; the Summenblock's own
// black rule right under it reads as one doubled hairline rather than as a boundary.
function markSummaryPageStarts(built, pages) {
  for (var p = 1; p < pages.length; p++) {
    var first = pages[p][0]
    if (first && first.kind === "block" && hasClass(first.nodes[0], "bd-summary")) {
      first.nodes[0].className = first.nodes[0].className + " bd-summary--page-start"
    }
  }
}

// Only the preview needs these filled: in the PDF wkhtmltopdf substitutes the same class
// names from the page parameters it passes to the band documents.
function numberPages(built) {
  for (var p = 0; p < built.length; p++) {
    setText(built[p].page.querySelectorAll(".page"), String(p + 1))
    setText(built[p].page.querySelectorAll(".topage"), String(built.length))
  }
}

function makePage(doc, template) {
  // <template> only exists in the preview; the PDF gets a bare page whose only job is to
  // force the break, since wkhtmltopdf still owns the page box and both bands.
  if (template && template.content && template.content.firstElementChild) {
    return template.content.firstElementChild.cloneNode(true)
  }
  var page = doc.createElement("div")
  page.className = "bd-page"
  return page
}

function contentSlot(page) {
  return page.querySelector(".bd-page__content") || page
}

function appendAll(host, nodes) {
  for (var i = 0; i < nodes.length; i++) host.appendChild(nodes[i])
}

// Takes a NodeList as readily as an array, and tolerates nulls in it — every caller is
// dropping scaffolding that may legitimately not be there.
function removeAll(nodes) {
  for (var i = 0; i < nodes.length; i++) {
    if (nodes[i] && nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i])
  }
}

function setText(elements, value) {
  for (var i = 0; i < elements.length; i++) elements[i].textContent = value
}

function hasClass(element, name) {
  return !!element && (" " + element.className + " ").indexOf(" " + name + " ") > -1
}
